import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Save,
  RefreshCcw,
  DollarSign,
  Percent,
  Shield,
  AlertTriangle,
  Settings,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface BetLimitSettings {
  minBet: number
  maxBet: number
  maxWinPerSpin: number
  maxWinPerDay: number
  maxWinPerMonth: number
}

interface GameSettings {
  slots: BetLimitSettings
  liveCasino: BetLimitSettings
  sports: BetLimitSettings
  fishing: BetLimitSettings
}

export function GameSettingsPage() {
  const [settings, setSettings] = useState<GameSettings>({
    slots: { minBet: 1, maxBet: 50000, maxWinPerSpin: 500000, maxWinPerDay: 1000000, maxWinPerMonth: 5000000 },
    liveCasino: { minBet: 10, maxBet: 500000, maxWinPerSpin: 2000000, maxWinPerDay: 5000000, maxWinPerMonth: 20000000 },
    sports: { minBet: 10, maxBet: 100000, maxWinPerSpin: 1000000, maxWinPerDay: 3000000, maxWinPerMonth: 15000000 },
    fishing: { minBet: 1, maxBet: 10000, maxWinPerSpin: 100000, maxWinPerDay: 500000, maxWinPerMonth: 2000000 },
  })

  const [generalSettings, setGeneralSettings] = useState({
    enableAutoPlay: true,
    enableQuickSpin: true,
    enableTurboMode: false,
    maxAutoSpins: 100,
    sessionTimeout: 60,
    showRTP: true,
    enableDemoMode: true,
    enableGameHistory: true,
    maintenanceMode: false,
    maintenanceMessage: 'ระบบกำลังปรับปรุง กรุณารอสักครู่',
  })

  const [rtpSettings, setRtpSettings] = useState({
    defaultRTP: 96.5,
    minRTP: 94,
    maxRTP: 98,
    vipRTPBonus: 0.5,
    newMemberRTPBonus: 1,
  })

  const [responsibleGaming, setResponsibleGaming] = useState({
    enableDailyLimit: true,
    defaultDailyLimit: 50000,
    enableWeeklyLimit: true,
    defaultWeeklyLimit: 200000,
    enableMonthlyLimit: true,
    defaultMonthlyLimit: 500000,
    enableCooldownPeriod: true,
    cooldownMinutes: 30,
    enableSelfExclusion: true,
  })

  const categoryNames: Record<string, string> = {
    slots: 'สล็อต',
    liveCasino: 'คาสิโนสด',
    sports: 'กีฬา',
    fishing: 'ยิงปลา',
  }

  const categoryIcons: Record<string, string> = {
    slots: '🎰',
    liveCasino: '🃏',
    sports: '⚽',
    fishing: '🐟',
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">ตั้งค่าเกม</h1>
          <p className="text-muted-foreground">กำหนดค่าเริ่มต้นและจำกัดการเดิมพัน</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <RefreshCcw className="h-4 w-4" />
            รีเซ็ต
          </Button>
          <Button className="gap-2">
            <Save className="h-4 w-4" />
            บันทึก
          </Button>
        </div>
      </div>

      {/* Bet Limits by Category */}
      <div className="grid gap-6 lg:grid-cols-2">
        {Object.entries(settings).map(([key, limits]) => (
          <Card key={key}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-xl">{categoryIcons[key]}</span>
                วงเงินเดิมพัน - {categoryNames[key]}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">เดิมพันขั้นต่ำ</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      type="number" 
                      value={limits.minBet} 
                      className="pl-9"
                      onChange={(e) => setSettings({
                        ...settings,
                        [key]: { ...limits, minBet: Number(e.target.value) }
                      })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">เดิมพันสูงสุด</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      type="number" 
                      value={limits.maxBet} 
                      className="pl-9"
                      onChange={(e) => setSettings({
                        ...settings,
                        [key]: { ...limits, maxBet: Number(e.target.value) }
                      })}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">ชนะสูงสุดต่อรอบ</Label>
                <Input 
                  type="number" 
                  value={limits.maxWinPerSpin}
                  onChange={(e) => setSettings({
                    ...settings,
                    [key]: { ...limits, maxWinPerSpin: Number(e.target.value) }
                  })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">ชนะสูงสุดต่อวัน</Label>
                  <Input 
                    type="number" 
                    value={limits.maxWinPerDay}
                    onChange={(e) => setSettings({
                      ...settings,
                      [key]: { ...limits, maxWinPerDay: Number(e.target.value) }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">ชนะสูงสุดต่อเดือน</Label>
                  <Input 
                    type="number" 
                    value={limits.maxWinPerMonth}
                    onChange={(e) => setSettings({
                      ...settings,
                      [key]: { ...limits, maxWinPerMonth: Number(e.target.value) }
                    })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            ตั้งค่าทั่วไป
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div className="space-y-0.5">
                <Label>Auto Play</Label>
                <p className="text-xs text-muted-foreground">อนุญาตให้เล่นอัตโนมัติ</p>
              </div>
              <Switch 
                checked={generalSettings.enableAutoPlay}
                onCheckedChange={(checked) => setGeneralSettings({...generalSettings, enableAutoPlay: checked})}
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div className="space-y-0.5">
                <Label>Quick Spin</Label>
                <p className="text-xs text-muted-foreground">หมุนเร็ว</p>
              </div>
              <Switch 
                checked={generalSettings.enableQuickSpin}
                onCheckedChange={(checked) => setGeneralSettings({...generalSettings, enableQuickSpin: checked})}
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div className="space-y-0.5">
                <Label>Turbo Mode</Label>
                <p className="text-xs text-muted-foreground">โหมดเทอร์โบ</p>
              </div>
              <Switch 
                checked={generalSettings.enableTurboMode}
                onCheckedChange={(checked) => setGeneralSettings({...generalSettings, enableTurboMode: checked})}
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div className="space-y-0.5">
                <Label>แสดง RTP</Label>
                <p className="text-xs text-muted-foreground">แสดงอัตราจ่ายในเกม</p>
              </div>
              <Switch 
                checked={generalSettings.showRTP}
                onCheckedChange={(checked) => setGeneralSettings({...generalSettings, showRTP: checked})}
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div className="space-y-0.5">
                <Label>Demo Mode</Label>
                <p className="text-xs text-muted-foreground">เล่นทดลอง</p>
              </div>
              <Switch 
                checked={generalSettings.enableDemoMode}
                onCheckedChange={(checked) => setGeneralSettings({...generalSettings, enableDemoMode: checked})}
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
              <div className="space-y-0.5">
                <Label>ประวัติเกม</Label>
                <p className="text-xs text-muted-foreground">บันทึกประวัติการเล่น</p>
              </div>
              <Switch 
                checked={generalSettings.enableGameHistory}
                onCheckedChange={(checked) => setGeneralSettings({...generalSettings, enableGameHistory: checked})}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 mt-6 pt-6 border-t">
            <div className="space-y-2">
              <Label className="text-sm">จำนวน Auto Spin สูงสุด</Label>
              <Input 
                type="number" 
                value={generalSettings.maxAutoSpins}
                onChange={(e) => setGeneralSettings({...generalSettings, maxAutoSpins: Number(e.target.value)})}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Session Timeout (นาที)</Label>
              <Input 
                type="number" 
                value={generalSettings.sessionTimeout}
                onChange={(e) => setGeneralSettings({...generalSettings, sessionTimeout: Number(e.target.value)})}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* RTP Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Percent className="h-5 w-5" />
            ตั้งค่า RTP
          </CardTitle>
          <CardDescription>กำหนดอัตราการจ่ายเงินรางวัล (Return To Player)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-5">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">RTP เริ่มต้น (%)</Label>
              <Input 
                type="number" 
                step="0.1"
                value={rtpSettings.defaultRTP}
                onChange={(e) => setRtpSettings({...rtpSettings, defaultRTP: Number(e.target.value)})}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">RTP ต่ำสุด (%)</Label>
              <Input 
                type="number" 
                step="0.1"
                value={rtpSettings.minRTP}
                onChange={(e) => setRtpSettings({...rtpSettings, minRTP: Number(e.target.value)})}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">RTP สูงสุด (%)</Label>
              <Input 
                type="number" 
                step="0.1"
                value={rtpSettings.maxRTP}
                onChange={(e) => setRtpSettings({...rtpSettings, maxRTP: Number(e.target.value)})}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">VIP Bonus (%)</Label>
              <Input 
                type="number" 
                step="0.1"
                value={rtpSettings.vipRTPBonus}
                onChange={(e) => setRtpSettings({...rtpSettings, vipRTPBonus: Number(e.target.value)})}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">New Member Bonus (%)</Label>
              <Input 
                type="number" 
                step="0.1"
                value={rtpSettings.newMemberRTPBonus}
                onChange={(e) => setRtpSettings({...rtpSettings, newMemberRTPBonus: Number(e.target.value)})}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Responsible Gaming */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            การเล่นอย่างรับผิดชอบ
          </CardTitle>
          <CardDescription>ตั้งค่าการจำกัดเพื่อป้องกันการเล่นเกินควร</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-3 p-4 rounded-lg bg-muted/30">
              <div className="flex items-center justify-between">
                <Label>จำกัดรายวัน</Label>
                <Switch 
                  checked={responsibleGaming.enableDailyLimit}
                  onCheckedChange={(checked) => setResponsibleGaming({...responsibleGaming, enableDailyLimit: checked})}
                />
              </div>
              <Input 
                type="number" 
                value={responsibleGaming.defaultDailyLimit}
                disabled={!responsibleGaming.enableDailyLimit}
                onChange={(e) => setResponsibleGaming({...responsibleGaming, defaultDailyLimit: Number(e.target.value)})}
              />
            </div>
            <div className="space-y-3 p-4 rounded-lg bg-muted/30">
              <div className="flex items-center justify-between">
                <Label>จำกัดรายสัปดาห์</Label>
                <Switch 
                  checked={responsibleGaming.enableWeeklyLimit}
                  onCheckedChange={(checked) => setResponsibleGaming({...responsibleGaming, enableWeeklyLimit: checked})}
                />
              </div>
              <Input 
                type="number" 
                value={responsibleGaming.defaultWeeklyLimit}
                disabled={!responsibleGaming.enableWeeklyLimit}
                onChange={(e) => setResponsibleGaming({...responsibleGaming, defaultWeeklyLimit: Number(e.target.value)})}
              />
            </div>
            <div className="space-y-3 p-4 rounded-lg bg-muted/30">
              <div className="flex items-center justify-between">
                <Label>จำกัดรายเดือน</Label>
                <Switch 
                  checked={responsibleGaming.enableMonthlyLimit}
                  onCheckedChange={(checked) => setResponsibleGaming({...responsibleGaming, enableMonthlyLimit: checked})}
                />
              </div>
              <Input 
                type="number" 
                value={responsibleGaming.defaultMonthlyLimit}
                disabled={!responsibleGaming.enableMonthlyLimit}
                onChange={(e) => setResponsibleGaming({...responsibleGaming, defaultMonthlyLimit: Number(e.target.value)})}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
              <div className="space-y-0.5">
                <Label>Cooldown Period</Label>
                <p className="text-xs text-muted-foreground">พักเล่นหลังแพ้ติดต่อกัน</p>
              </div>
              <div className="flex items-center gap-2">
                <Input 
                  type="number" 
                  value={responsibleGaming.cooldownMinutes}
                  className="w-20"
                  disabled={!responsibleGaming.enableCooldownPeriod}
                  onChange={(e) => setResponsibleGaming({...responsibleGaming, cooldownMinutes: Number(e.target.value)})}
                />
                <span className="text-sm text-muted-foreground">นาที</span>
                <Switch 
                  checked={responsibleGaming.enableCooldownPeriod}
                  onCheckedChange={(checked) => setResponsibleGaming({...responsibleGaming, enableCooldownPeriod: checked})}
                />
              </div>
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
              <div className="space-y-0.5">
                <Label>Self Exclusion</Label>
                <p className="text-xs text-muted-foreground">อนุญาตให้ระงับตัวเอง</p>
              </div>
              <Switch 
                checked={responsibleGaming.enableSelfExclusion}
                onCheckedChange={(checked) => setResponsibleGaming({...responsibleGaming, enableSelfExclusion: checked})}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Maintenance Mode */}
      <Card className={cn(generalSettings.maintenanceMode && "border-destructive")}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className={cn("h-5 w-5", generalSettings.maintenanceMode && "text-destructive")} />
            โหมดปรับปรุง
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-destructive/5 border border-destructive/20">
            <div className="space-y-0.5">
              <Label className="text-destructive">เปิดโหมดปรับปรุง</Label>
              <p className="text-xs text-muted-foreground">ปิดการเข้าถึงเกมทั้งหมดชั่วคราว</p>
            </div>
            <Switch 
              checked={generalSettings.maintenanceMode}
              onCheckedChange={(checked) => setGeneralSettings({...generalSettings, maintenanceMode: checked})}
            />
          </div>
          <div className="space-y-2">
            <Label>ข้อความแจ้งเตือน</Label>
            <Input 
              value={generalSettings.maintenanceMessage}
              onChange={(e) => setGeneralSettings({...generalSettings, maintenanceMessage: e.target.value})}
              placeholder="ระบุข้อความที่จะแสดงให้ผู้เล่นเห็น"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}










